'use client'

import { GripVerticalIcon, XIcon } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { type ReactNode, useCallback, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  composeOptionTicker,
  filterAndGroupInstruments,
  getNearesetExpiry,
  resolveOptionTickerQuoteCurrency,
} from '@/features/chart/lib/utils/option-util'
import { BuySellSelector } from '@/features/order/components/buy-sell-selector'
import ExpiryDropdown from '@/features/order/order-form/components/dropdown/expiry'
import { InstrumentKindDropdown } from '@/features/order/order-form/components/dropdown/instrument-kind'
import { QuoteAssetDropdown } from '@/features/order/order-form/components/dropdown/quote-asset'
import StrikePriceDropdown, {
  formatOptionStrike,
} from '@/features/order/order-form/components/dropdown/strike-price'
import { LeverageChip } from '@/features/order/order-form/components/leverage-chip'
import { InputWithContract } from '@/features/order/order-form/components/widget/input-with-contract'
import {
  InputWithTickerList,
  TickerInfo,
} from '@/features/order/order-form/components/widget/input-with-ticker-list'
import { useOrderFormTradeVenue } from '@/features/order/order-form/hooks/use-order-form-trade-venue'
import {
  findInstrument,
  UnifiedInstrumentKind,
} from '@/features/order/order-form/lib/instrument-filter'
import {
  resolveBaseCurrency,
  resolveQuoteCurrency,
} from '@/features/order/order-form/lib/instrument-resolution'
import { parseSymbolExpiry } from '@/features/order/order-form/lib/parse-instrument-symbol'
import {
  formatExpiryDate,
  resolveOptionInstrument,
} from '@/features/order/order-form/lib/parse-option-instrument'
import { useNumberFormat } from '@/hooks/use-number-format'
import { parseExchangeField } from '@/lib/proto/util'
import { cn, safeParseFloat } from '@/lib/utils'
import { floorQuantityToStep } from '@/lib/utils/qty-precision'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { useInstrumentList } from '@/service/instrument.hook'
import { SelectedOption } from '@/types/dashboard'
import { Exchange, InstrumentKind } from '@/types/generated/common/types'

interface SizeShortcut {
  amount: string
  ticker: string
  displayAmount?: string
}

function findOptionSymbolForSelection(
  instruments: ReturnType<typeof filterAndGroupInstruments>,
  ticker: string,
  type: 'C' | 'P',
  indexPrice: number,
  preferredExpiry?: string,
  preferredStrike?: number
): string | null {
  const group = instruments.get(ticker)
  if (!group || group.expires.size === 0) return null

  if (preferredExpiry && preferredStrike != null) {
    const exact = group.expires
      .get(preferredExpiry)
      ?.find((entry) => entry.type === type && entry.strike === preferredStrike)
    if (exact) return exact.symbol
  }

  const expiries = [...group.expires.keys()]
  const expiry =
    (preferredExpiry && group.expires.has(preferredExpiry) ? preferredExpiry : null) ??
    getNearesetExpiry(expiries) ??
    expiries[0]
  const candidates = (group.expires.get(expiry) ?? []).filter((entry) => entry.type === type)
  if (candidates.length === 0) return null

  if (preferredStrike != null) {
    const sameStrike = candidates.find((entry) => entry.strike === preferredStrike)
    if (sameStrike) return sameStrike.symbol
  }

  const selected =
    indexPrice > 0
      ? candidates.reduce((closest, candidate) =>
          Math.abs(candidate.strike - indexPrice) < Math.abs(closest.strike - indexPrice)
            ? candidate
            : closest
        )
      : candidates[Math.floor(candidates.length / 2)]
  return selected.symbol
}

interface OrderRowProps {
  // Card this row's order form belongs to — used to resolve the row's own
  // fallback exchange (this card's focused watch-list tab) instead of the
  // globally-selected account, when the row hasn't stamped its own `exchange`.
  cardId?: string
  // Common props
  mode: 'perp' | 'option'
  side: 'buy' | 'sell'
  onSideChange: (side: 'buy' | 'sell') => void
  value: string
  onValueChange: (value: string) => void
  tickers: TickerInfo[]
  selectedTicker: string
  onTickerChange: (ticker: string) => void
  denominationMap?: Record<string, string>
  onDenominationMapChange?: (map: Record<string, string>) => void
  disabled?: boolean
  index?: number
  optionIndex?: number
  autoFocus?: boolean
  amountInputIndex?: number
  // Close-position row on a venue that closes the full position only: the size box is read-only.
  lockedAmount?: boolean

  // Perpetual specific props
  symbol?: string
  lastPrice?: string
  instrumentKind?: UnifiedInstrumentKind | string
  // Already-resolved base/quote from the caller — takes precedence over
  // resolveBaseCurrency/resolveQuoteCurrency's own lookup below (which now
  // uses this row's exchange-scoped instrumentMap, see `exchange` below, but
  // an explicit value from the caller is still preferred when available).
  baseCurrency?: string
  quoteCurrency?: string
  // Stringified numeric Exchange enum (e.g. "12" for Upbit), same convention
  // as SelectedInstrument.exchange/UnifiedItemValues.exchange — the row's own
  // exchange, used to fetch this row's own exchange-scoped instrument list
  // (quote/kind pickers, findInstrument lookups) instead of whichever account
  // happens to be globally active. Absent for option rows (out of scope) and
  // any not-yet-migrated caller, in which case the active account's exchange
  // is used as a fallback.
  exchange?: string
  // The account this row trades on. Only read for Papertrade perp rows, where it keys the
  // leverage chip; every other venue ignores it.
  accountId?: string

  // Option specific props
  option?: SelectedOption

  // Instrument change
  onSymbolChange?: (newSymbol: string) => void
  // Remove callback
  onRemove?: () => void
  // Drag-to-reorder
  onDragHandlePointerDown?: (e: React.PointerEvent<HTMLDivElement>) => void
  isDragging?: boolean
  // Quantity increment step derived from instrument's qtyPrecision
  qtyStep?: number
  // Validation error for minimum trade amount (options only)
  minTradeAmountError?: string
  // Base-denominated shortcuts rendered below the size field. The current
  // shortcut is absent when there is no open position/holding; max is absent
  // while the batch preview has no computable result.
  currentSize?: SizeShortcut
  maxSize?: SizeShortcut
  // Spreader mode only: which role this leg plays. Undefined outside spreader mode, in which
  // case the toggle doesn't render at all.
  spreaderRole?: 'quoting' | 'taking'
  onSpreaderRoleChange?: (role: 'quoting' | 'taking') => void
  // Display only (the RFQ popup's legs): the same row, with the pickers as plain labels, the
  // side fixed and the amount a static box. Needs no order-form context, so it renders
  // outside an OrderFormProvider.
  readOnly?: boolean
  /** Read-only mode only: a figure under the amount box, where the editable row shows its
   *  Current/Max hints (the RFQ popup's per-leg mark price). */
  readOnlyNote?: ReactNode
}

/**
 * Unified order row component for both perpetual and option orders
 * Layout:
 *   Left:  [border bar] | [ticker name + order detail (top)] / [side selector + expiry (bottom)]
 *   Right: [input] [x button]
 */
export function OrderRow({
  cardId,
  mode,
  side,
  onSideChange,
  value,
  onValueChange,
  tickers,
  selectedTicker,
  onTickerChange,
  denominationMap,
  onDenominationMapChange,
  disabled,
  index = 0,
  optionIndex,
  symbol,
  lastPrice,
  option,
  instrumentKind,
  baseCurrency,
  quoteCurrency,
  exchange,
  accountId,
  autoFocus = false,
  amountInputIndex,
  lockedAmount = false,
  onSymbolChange,
  onRemove,
  onDragHandlePointerDown,
  isDragging = false,
  qtyStep,
  minTradeAmountError,
  currentSize,
  maxSize,
  spreaderRole,
  onSpreaderRoleChange,
  readOnly = false,
  readOnlyNote,
}: OrderRowProps) {
  const t = useTranslations('orderForm')
  const locale = useLocale()
  const fmt = useNumberFormat()
  const [localDenominationMap, setLocalDenominationMap] = useState<Record<string, string>>({})
  const [isRowHovered, setIsRowHovered] = useState(false)

  // Row-scoped instrument list: fetched by this row's OWN exchange (from the
  // `exchange` prop), not the globally-selected account's — falls back to the
  // card's own focused-tab exchange only for rows that haven't been migrated
  // to stamp their own `exchange` yet. See parseExchangeField's doc comment
  // for why this can't just be Number(exchange).
  const cardTradeVenue = useOrderFormTradeVenue(cardId)
  const accountExchange = useAccountPrimaryExchange(cardTradeVenue)
  const rowExchange = parseExchangeField(exchange, accountExchange)
  const isPapertradePerp =
    !readOnly && mode === 'perp' && rowExchange === Exchange.EXCHANGE_PAPERTRADE && !!symbol
  const { data: rowInstrumentListData } = useInstrumentList(rowExchange)
  const instruments = useMemo(
    () => rowInstrumentListData?.instruments ?? [],
    [rowInstrumentListData]
  )
  const instrumentMap = useMemo(() => new Map(instruments.map((i) => [i.symbol, i])), [instruments])

  const isLong = side === 'buy'

  // The exchange's own structured instrument metadata for this option, when it's in the
  // current list — needed because `parseOptionInstrument`'s regex only recognizes
  // Deribit-style symbols (e.g. "BTC-28JUN24-70000-C"). Bybit and other venues use a
  // different symbol format, which the regex silently fails to match; passing this
  // instrument through to `resolveOptionInstrument` lets it read strike/expiry/type from
  // the proto fields instead, so the row doesn't fall back to a hardcoded ticker.
  const optionInstrument = useMemo(
    () =>
      mode === 'option' && option
        ? instruments.find((i) => i.symbol === option.instrumentName)
        : undefined,
    [mode, option, instruments]
  )

  // Parse option data if in option mode
  const parsed =
    mode === 'option' && option
      ? resolveOptionInstrument(option.instrumentName, optionInstrument)
      : null

  // Contract size for options (e.g. TRX options have contractSize = 10000)
  const optionContractSize = useMemo(() => {
    const cs = Number(optionInstrument?.contractSize)
    return cs > 1 ? cs : undefined
  }, [optionInstrument])
  const optionType = parsed?.type === 'C' ? 'Call' : 'Put'
  // Use baseCurrency (e.g., "BTC") not ticker (e.g., "BTC_USDC") for contract display
  const targetTicker = parsed?.baseCurrency || 'BTC'
  // Expiry label for futures (null for perpetuals, spot, and options)
  const expiryLabel = useMemo(() => {
    if (mode === 'option') return null
    return parseSymbolExpiry(symbol || '')
  }, [mode, symbol])

  // Handle side change - atom updates are handled by parent via onSideChange
  const handleSideChange = (newIsLong: boolean) => {
    const newSide = newIsLong ? 'buy' : 'sell'
    onSideChange(newSide)
  }

  const handleSizeShortcut = ({ amount, ticker }: SizeShortcut) => {
    if (disabled || lockedAmount) return

    const normalizedAmount = floorQuantityToStep(amount, qtyStep)
    if (!normalizedAmount) return

    onTickerChange(ticker)
    onValueChange(normalizedAmount)

    const currentMap = denominationMap || localDenominationMap
    const nextMap = { ...currentMap, [ticker]: normalizedAmount }
    if (onDenominationMapChange) onDenominationMapChange(nextMap)
    else setLocalDenominationMap(nextMap)
  }

  const { base, quote, kind } = useMemo(() => {
    if (mode === 'option' && parsed) {
      return {
        base: parsed.baseCurrency,
        quote: resolveOptionTickerQuoteCurrency(parsed.ticker),
        kind: (parsed.type === 'C' ? 'option-call' : 'option-put') as UnifiedInstrumentKind,
      }
    }

    const baseTicker = baseCurrency || (symbol ? resolveBaseCurrency(instrumentMap, symbol) : 'BTC')
    const quoteTicker =
      quoteCurrency || (symbol ? resolveQuoteCurrency(instrumentMap, symbol) : 'USD')

    const lowerKind = instrumentKind?.toLowerCase()
    const kindFromMetadata: UnifiedInstrumentKind | null =
      lowerKind === 'spot'
        ? 'spot'
        : lowerKind === 'perp' || lowerKind === 'perps' || lowerKind === 'perpetual'
          ? 'perp'
          : lowerKind === 'future' || lowerKind === 'futures'
            ? 'future'
            : null
    const catalogInstrument = symbol ? instrumentMap.get(symbol) : undefined
    const kindFromCatalog: UnifiedInstrumentKind | null =
      catalogInstrument?.kind === InstrumentKind.INSTRUMENT_KIND_SPOT
        ? 'spot'
        : catalogInstrument?.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE
          ? !catalogInstrument.expirationTsMs
            ? 'perp'
            : 'future'
          : null
    // Symbols are venue-specific identifiers, not product metadata. In particular,
    // Bybit's `BTCPERP` must not be parsed as spot just because it lacks `-PERP`.
    const currentKind = kindFromMetadata ?? kindFromCatalog ?? 'future'

    return { base: baseTicker, quote: quoteTicker, kind: currentKind }
  }, [mode, parsed, symbol, instrumentKind, instrumentMap, baseCurrency, quoteCurrency])

  const handleQuoteChange = useCallback(
    (newQuote: string) => {
      if (newQuote === quote || !onSymbolChange) return

      if ((kind === 'option-call' || kind === 'option-put') && parsed) {
        const grouped = filterAndGroupInstruments(instruments)
        const targetSymbol = findOptionSymbolForSelection(
          grouped,
          composeOptionTicker(base.toUpperCase(), newQuote.toUpperCase()),
          kind === 'option-call' ? 'C' : 'P',
          safeParseFloat(lastPrice ?? '') || 0,
          parsed.expiry,
          parsed.strike
        )
        if (targetSymbol) onSymbolChange(targetSymbol)
        return
      }

      // Keep current kind if available for new quote
      const inst = findInstrument(instruments, base, newQuote, kind)
      if (inst) {
        onSymbolChange(inst.symbol)
        return
      }
      // Fallback: try kinds in priority order
      const fallbackKinds: UnifiedInstrumentKind[] = [
        'perp',
        'spot',
        'future',
        'option-call',
        'option-put',
      ]
      for (const fallbackKind of fallbackKinds) {
        if (fallbackKind === kind) continue
        const fallbackInst = findInstrument(instruments, base, newQuote, fallbackKind)
        if (fallbackInst) {
          onSymbolChange(fallbackInst.symbol)
          return
        }
      }
    },
    [base, kind, instruments, lastPrice, onSymbolChange, parsed, quote]
  )

  const handleKindChange = useCallback(
    (newKind: UnifiedInstrumentKind) => {
      if (newKind === kind || !onSymbolChange) return

      if (newKind === 'option-call' || newKind === 'option-put') {
        const grouped = filterAndGroupInstruments(instruments)
        const targetSymbol = findOptionSymbolForSelection(
          grouped,
          composeOptionTicker(base.toUpperCase(), quote.toUpperCase()),
          newKind === 'option-call' ? 'C' : 'P',
          safeParseFloat(lastPrice ?? '') || 0,
          parsed?.expiry,
          parsed?.strike
        )
        if (targetSymbol) onSymbolChange(targetSymbol)
        return
      }

      const inst = findInstrument(instruments, base, quote, newKind)
      if (inst) onSymbolChange(inst.symbol)
    },
    [base, kind, instruments, lastPrice, onSymbolChange, parsed, quote]
  )

  return (
    <div
      data-tutorial={spreaderRole}
      className={cn([
        'font-ibm-plex-sans @container relative flex items-center justify-between gap-2 transition-all',
        isDragging && 'border-foreground/40 border',
      ])}
      style={{
        zIndex: isDragging ? 100 : 30 - index,
        background: isDragging ? '#0A292AE5' : undefined,
      }}
      onMouseEnter={() => setIsRowHovered(true)}
      onMouseLeave={() => setIsRowHovered(false)}
    >
      {/* Left side: Clickable border + Info + Side selector */}
      <div className="flex h-full min-w-0 flex-1 items-center gap-1">
        {/* Drag handle - visible on hover */}
        {onDragHandlePointerDown && (
          <div
            className={cn(
              'absolute -left-0.5 flex shrink-0 -translate-x-full cursor-grab touch-none items-center text-white/30 transition-opacity hover:text-white/60 active:cursor-grabbing',
              isRowHovered || isDragging ? 'flex' : 'hidden'
            )}
            onPointerDown={(e) => {
              e.preventDefault()
              onDragHandlePointerDown(e)
            }}
          >
            <GripVerticalIcon className="size-3" />
          </div>
        )}
        {/* Clickable border bar to toggle side */}
        {readOnly ? (
          <span
            className={cn([
              'h-11 w-0.5 shrink-0 self-stretch',
              isLong ? 'bg-chart-mint' : 'bg-chart-red',
            ])}
          />
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              handleSideChange(!isLong)
            }}
            tabIndex={-1}
            className={cn([
              'h-11 w-0.5 shrink-0 self-stretch transition-colors hover:opacity-80',
              isLong ? 'bg-chart-mint' : 'bg-chart-red',
            ])}
            aria-label={isLong ? t('switchToShort') : t('switchToLong')}
          />
        )}
        <div className="flex h-full flex-col justify-between pl-1">
          <div className="flex w-full items-center gap-1">
            <div className="flex items-center gap-0.5">
              <span className="text-sm font-semibold text-white">{base}</span>
              <p className="text-sm font-semibold text-white">/</p>
            </div>
            {readOnly ? (
              <>
                <span className="font-ibm-plex-sans text-sm font-medium text-white">{quote}</span>
                <span className="font-ibm-plex-sans text-foreground/50 shrink-0 text-sm whitespace-nowrap">
                  {t(`instrumentKindShort.${kind}`)}
                </span>
                {mode === 'option' && parsed && (
                  <span className="font-ibm-plex-sans text-xs font-medium text-white">
                    {formatOptionStrike(parsed.strike, parsed.ticker)}
                  </span>
                )}
              </>
            ) : (
              <>
                <QuoteAssetDropdown
                  base={base}
                  quote={quote}
                  kind={kind}
                  onQuoteChange={handleQuoteChange}
                  instruments={instruments}
                />
                <InstrumentKindDropdown
                  base={base}
                  quote={quote}
                  kind={kind}
                  onKindChange={handleKindChange}
                  instruments={instruments}
                />
                {mode === 'option' && option && (
                  <StrikePriceDropdown option={option} index={optionIndex ?? index} />
                )}
              </>
            )}
          </div>

          {/* Second Row: Buy/Sell (Long/Short) | Expiry Date */}
          <div className="flex items-center gap-1">
            <BuySellSelector
              className="w-fit min-w-0 p-0"
              oppositeClassName="hover:bg-foreground/10"
              type={'spot'}
              isLong={isLong}
              onChange={handleSideChange}
              quickMode
              tabIndex={-1}
              disabled={readOnly}
            />
            {spreaderRole && onSpreaderRoleChange && (
              <button
                type="button"
                // Only ever promotes THIS leg to quoting — never demotes it directly. With
                // exactly one quoting leg allowed among N, "click the quoting badge to make
                // it taking" has no well-defined target to promote instead, and used to
                // (2-leg only) rely on there being exactly one other leg to flip. Clicking a
                // taking leg's badge already produces the same end state for the 2-leg case
                // (it promotes the other leg, demoting this one), so disabling the reverse
                // click loses no capability there and fixes the N-leg case, where the old
                // toggle would have promoted every other leg to quoting at once.
                disabled={spreaderRole === 'quoting'}
                onClick={() => onSpreaderRoleChange('quoting')}
                tabIndex={-1}
                title={spreaderRole === 'quoting' ? t('thisLegIsQuoting') : t('makeThisLegQuoting')}
                className={cn(
                  'px-1.5 py-0.5 text-[10px] font-semibold tracking-tight uppercase',
                  spreaderRole === 'quoting'
                    ? 'bg-chart-mint/20 text-chart-mint cursor-default'
                    : 'bg-white/5 text-white/50 hover:text-white/80'
                )}
              >
                {spreaderRole === 'quoting' ? t('quoting') : t('taking')}
              </button>
            )}
            {readOnly && mode === 'option' && parsed && (
              <span className="font-ibm-plex-sans text-sm whitespace-nowrap text-white/50">
                {formatExpiryDate(parsed.expiry, locale)}
              </span>
            )}
            {!readOnly && mode === 'option' && option && (
              <ExpiryDropdown option={option} index={optionIndex ?? index} lastPrice={lastPrice} />
            )}
            {readOnly && mode === 'perp' && expiryLabel && (
              <span className="font-ibm-plex-sans text-xs text-white/40">
                {formatExpiryDate(expiryLabel, locale)}
              </span>
            )}
            {!readOnly && mode === 'perp' && kind === 'future' && symbol && (
              <ExpiryDropdown
                mode="future"
                base={base}
                quote={quote}
                symbol={symbol}
                onSymbolChange={onSymbolChange}
              />
            )}
            {!readOnly && mode === 'perp' && kind !== 'future' && expiryLabel && (
              <span className="font-ibm-plex-sans text-xs text-white/40">
                {formatExpiryDate(expiryLabel, locale)}
              </span>
            )}
            {isPapertradePerp && accountId && (
              <LeverageChip
                accountId={accountId}
                symbol={symbol}
                maxLeverage={instrumentMap.get(symbol)?.maxLeverage}
              />
            )}
          </div>
        </div>
      </div>

      {/* Right side: Input + Remove button */}
      <div className="flex shrink-0 items-center gap-1">
        <div className="relative">
          {readOnly ? (
            <div className="flex flex-col items-end gap-0.5">
              <div className="border-foreground/10 flex h-9 w-36 items-center border text-sm font-medium @[360px]:w-44 @[400px]:w-52 @[450px]:w-72">
                <span className="text-foreground min-w-0 flex-1 truncate px-3 text-right">
                  {fmt.number(value, { maximumFractionDigits: 8, sensitive: true })}
                </span>
                <span className="bg-foreground/10 h-full w-px shrink-0" />
                <span className="text-foreground/60 flex h-full min-w-18.5 shrink-0 items-center justify-center px-3">
                  {mode === 'option' ? targetTicker : selectedTicker}
                </span>
              </div>
              {/* Where the editable row shows its Current/Max hints. */}
              {readOnlyNote}
            </div>
          ) : mode === 'option' ? (
            <InputWithContract
              value={value}
              onValueChange={onValueChange}
              targetTicker={targetTicker}
              contractSize={optionContractSize}
              placeholder="0"
              className="w-36 @[360px]:w-44 @[400px]:w-52 @[450px]:w-72"
              disabled={disabled}
              autoFocus={autoFocus}
              amountInputIndex={amountInputIndex}
              readOnly={lockedAmount}
              title={lockedAmount ? t('closeFullOnly') : undefined}
              showNumberPad
              step={qtyStep ?? 1}
            />
          ) : (
            <InputWithTickerList
              value={value}
              onValueChange={onValueChange}
              tickers={tickers}
              selectedTicker={selectedTicker}
              onTickerChange={onTickerChange}
              denominationMap={denominationMap || localDenominationMap}
              onDenominationMapChange={onDenominationMapChange || setLocalDenominationMap}
              placeholder="0"
              className="border-foreground/10 w-36 border @[360px]:w-44 @[400px]:w-52 @[450px]:w-72"
              disabled={disabled}
              autoFocus={autoFocus}
              amountInputIndex={amountInputIndex}
              readOnly={lockedAmount}
              title={lockedAmount ? t('closeFullOnly') : undefined}
              showNumberPad
              step={qtyStep}
            />
          )}
          {!minTradeAmountError && (currentSize || maxSize) && (
            <div className="absolute top-full left-0 mt-0.5 flex w-full justify-between gap-2 text-xs text-white/40">
              {currentSize ? (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSizeShortcut(currentSize)}
                  tabIndex={-1}
                  className="truncate text-left underline-offset-2 transition-colors not-disabled:cursor-pointer hover:text-white/70 hover:underline disabled:cursor-default"
                >
                  {t('currentSizeHintLabel', {
                    amount: fmt.text(currentSize.displayAmount ?? currentSize.amount, {
                      sensitive: true,
                    }),
                  })}
                </button>
              ) : (
                <span />
              )}
              {maxSize ? (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSizeShortcut(maxSize)}
                  tabIndex={-1}
                  className="truncate text-right underline-offset-2 transition-colors not-disabled:cursor-pointer hover:text-white/70 hover:underline disabled:cursor-default"
                >
                  {t('maxSizeHintLabel', {
                    amount: fmt.text(maxSize.displayAmount ?? maxSize.amount, { sensitive: true }),
                  })}
                </button>
              ) : null}
            </div>
          )}
          {minTradeAmountError && (
            <p className="text-destructive absolute top-full right-0 mt-0.5 text-xs">
              {minTradeAmountError}
            </p>
          )}
        </div>
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={onRemove}
            tabIndex={-1}
            className="flex size-4 shrink-0 items-center justify-center text-white/30 transition-colors not-disabled:cursor-pointer hover:text-white"
            aria-label={t('removeOrderRow')}
          >
            <XIcon className="size-3" />
          </Button>
        )}
      </div>
    </div>
  )
}
